import { ChevronDown, CornerDownRight } from "lucide-react";
import { useCallback, useMemo, useState } from "react";
import type { GetArtifactResp } from "../../types/artifacts.ts";
import { ActionBar } from "../common/ActionBar/ActionBar.tsx";
import type { SendMessageFn } from "../messages/hooks/useMessageList.tsx";
import { useRelocatedBiscuitIds } from "../messages/relocatedBiscuitContext.tsx";
import { isUnansweredForm } from "@/shared/selectOmniArtifacts.ts";
import { ArtifactItem } from "./ArtifactItem.tsx";
import { artifactsHaveBreadcrumbs } from "./artifactUtils.ts";

type ArtifactGroupProps = {
  messageId: number;
  threadId: number;
  artifacts: GetArtifactResp[];
  messageContent: string;
  sendMessage: SendMessageFn;
  shouldStreamContent?: boolean;
  hideFooter?: boolean;
};

/**
 * Keeps shopping recommendations visible and groups other artifacts from the
 * same message under one collapsible breadcrumb.
 */
export const ArtifactGroup = ({
  messageId,
  threadId,
  artifacts,
  messageContent,
  sendMessage,
  shouldStreamContent = false,
  hideFooter = false,
}: ArtifactGroupProps) => {
  // An open biscuit renders beneath the thread's latest response, not here. The
  // breadcrumb stays where it is either way — it's the record of what okthx did
  // — but with nothing left under it there is nothing to expand.
  const relocatedIds = useRelocatedBiscuitIds();
  const inlineArtifacts = useMemo(
    () => artifacts.filter((a) => !relocatedIds.has(a.id)),
    [artifacts, relocatedIds],
  );

  const shoppingArtifacts = inlineArtifacts.filter((a) => a.artifactType === "shoppingResults");
  const groupedArtifacts = useMemo(
    () => inlineArtifacts.filter((a) => a.artifactType !== "shoppingResults"),
    [inlineArtifacts],
  );

  // Expand by default when an artifact needs the user's attention; resolved
  // artifacts collapse to a breadcrumb. Status-driven: a `waitingForUserAction`
  // artifact stays expanded in-chat even while it's also surfaced in the
  // omni-artifact overlay/queue — the overlay no longer force-collapses it.
  const hasArtifactNeedingAttention = useMemo(
    () =>
      groupedArtifacts.some(
        (artifact) =>
          (artifact.isHeavy && artifact.status === "waitingForUserAction") ||
          // An active booking (in progress / waiting / failed) stays expanded so
          // the user sees it inline; a resolved one (confirmed / cancelled)
          // collapses to a breadcrumb like other settled artifacts.
          (artifact.isHeavy &&
            artifact.artifactType === "booking" &&
            (artifact.status === "pending" ||
              artifact.status === "waiting" ||
              artifact.status === "failed")) ||
          // A routine is installed the moment it is saved, so its card is the
          // only place the user sees what was set up — there was no proposal to
          // read first. Other settled artifacts collapse because the user
          // already acted on them; nobody has acted on this one.
          (artifact.isHeavy &&
            artifact.artifactType === "routineArtifact" &&
            artifact.status === "approved") ||
          // Same for a skill, and for the same reason (BIG-7890): it is written
          // when the agent writes it, so the card is the only place the user
          // sees the text that now runs. Collapsing it to a breadcrumb would
          // hide the one thing they might want to correct.
          (artifact.isHeavy &&
            artifact.artifactType === "skillArtifact" &&
            artifact.status === "approved") ||
          // And for a document (BIG-8046): saved as the agent writes it, so the
          // card is the only place the user reads what was written.
          (artifact.isHeavy &&
            artifact.artifactType === "document" &&
            artifact.status === "approved") ||
          // And for an applet (BIG-8167): saved as the agent builds it, so the
          // card is where the user first runs what was built.
          (artifact.isHeavy &&
            artifact.artifactType === "applet" &&
            artifact.status === "approved") ||
          (!artifact.isHeavy && artifact.artifactType === "userResponse") ||
          isUnansweredForm(artifact),
      ),
    [groupedArtifacts],
  );
  const shouldShowMessageContent = useMemo(() => artifactsHaveBreadcrumbs(artifacts), [artifacts]);

  const [isExpanded, setIsExpanded] = useState(hasArtifactNeedingAttention);

  const closeArtifactGroup = useCallback(() => {
    if (!shouldShowMessageContent) return;
    setIsExpanded(false);
  }, [shouldShowMessageContent]);

  // Every artifact in this message is in the overlay — render nothing inline.
  if (artifacts.length === 0) return null;

  const isCollapsible = groupedArtifacts.length > 0;
  const lastInlineCreatedAt = inlineArtifacts.at(-1)?.createdAt;

  // Nothing to reveal and no breadcrumb copy to reveal it with: the message has
  // handed everything it had to the anchored biscuits, so it renders nothing.
  if (!isCollapsible && !messageContent && shoppingArtifacts.length === 0) return null;

  return (
    <div className="w-full flex flex-col gap-2">
      {/* Recommendations stay visible even when the steps return a held card here. */}
      {shoppingArtifacts.map((artifact) => (
        <ArtifactItem
          key={artifact.id}
          messageId={messageId}
          threadId={threadId}
          msgListArtifact={artifact}
          sendMessage={sendMessage}
          hideFooter
        />
      ))}
      {shouldShowMessageContent &&
        (isCollapsible ? (
          <div
            onClick={() => setIsExpanded(!isExpanded)}
            className="flex items-start gap-1 text-xs text-muted-foreground"
            data-state={isExpanded ? "open" : "closed"}
          >
            <CornerDownRight className="size-3.5 stroke-1.5 mt-0.5 shrink-0" />
            <button
              onClick={() => setIsExpanded(!isExpanded)}
              className="text-xs text-left text-muted-foreground hover:text-foreground transition-colors CollapsibleTrigger"
              data-state={isExpanded ? "open" : "closed"}
            >
              {messageContent}
              <span className="whitespace-nowrap">
                &nbsp;
                <ChevronDown className="size-3.5 shrink-0 transition-transform duration-200 cursor-pointer CollapsibleTriggerIcon inline-block" />
              </span>
            </button>
          </div>
        ) : (
          // Same row, minus the affordance: the biscuit it would have revealed
          // is rendered under the response, so a chevron here would open onto
          // nothing.
          <div className="flex items-start gap-1 text-xs text-muted-foreground">
            <CornerDownRight className="size-3.5 stroke-1.5 mt-0.5 shrink-0" />
            <span className="text-xs text-left">{messageContent}</span>
          </div>
        ))}

      {isExpanded && isCollapsible && (
        <div className="flex flex-col gap-5">
          {groupedArtifacts.map((artifact, index) => (
            <ArtifactItem
              key={artifact.id}
              messageId={messageId}
              threadId={threadId}
              msgListArtifact={artifact}
              sendMessage={sendMessage}
              shouldStreamContent={shouldStreamContent}
              closeArtifactGroup={closeArtifactGroup}
              // When breadcrumbs are shown, always hide footer on artifacts
              // (ActionBar is rendered below, outside the collapsible)
              hideFooter={
                shouldShowMessageContent || hideFooter || index !== groupedArtifacts.length - 1
              }
            />
          ))}
        </div>
      )}

      {/* When breadcrumbs are shown, render ActionBar outside the collapsible section */}
      {shouldShowMessageContent && !hideFooter && (
        <ActionBar
          item={{
            // The last artifact still rendered here. Reading the unfiltered list
            // would timestamp this bar off a card that relocated away.
            date: lastInlineCreatedAt ? new Date(lastInlineCreatedAt) : new Date(),
            messageId,
            threadId,
            isUserMessage: false,
            hideCopyAction: true,
          }}
          sendMessage={sendMessage}
          hoverOnly
        />
      )}
    </div>
  );
};
